import React from 'react';
import Module from 'Components/Core/Module';
import IbBrokerageOrderForm from 'Components/Banking/IbBrokerageOrderForm';
import { registerBrokerageDataListener, notifyBrokerageDataChanged } from 'Components/Banking/brokerageDataBus';
import './Brokerage.css';

// IB-Orderbuch (Chat vom 2026-08-07, Phase 2 der IBKR-Integration) - Pendant
// zu BrokerageOrderbook.js (Comdirect), aber ohne Verbindungsauswahl/TAN -
// siehe IbBrokerageOverview.js-Kommentar. Zeigt die komplette lokale
// Order-Historie (ibBrokerageOrders), Stornieren nur bei offenem Status.
//
// Chat vom 2026-08-07 (Nutzerfeedback): Filter-Tabs und sprechende
// Text-Status (statt Farbpunkt) analog BrokerageOrderbook.js (Comdirect) -
// IBs eigene Status-Strings (siehe TWS-API-Doku "Order.orderStatus") sind
// technischer Natur, werden hier auf verstaendliche deutsche Begriffe
// abgebildet.
const IB_STATUS_LABELS = {
	PendingSubmit: 'Wird übermittelt',
	PendingCancel: 'Storno wird verarbeitet',
	PreSubmitted: 'Vorgemerkt',
	Submitted: 'Übermittelt',
	ApiPending: 'Wird verarbeitet',
	ApiCancelled: 'Storniert',
	Cancelled: 'Storniert',
	Filled: 'Ausgeführt',
	Inactive: 'Inaktiv',
	Pending: 'Ausstehend',
};

const OPEN_STATUSES = ['PendingSubmit', 'PreSubmitted', 'Submitted', 'ApiPending', 'Pending', 'PendingCancel'];
const EXECUTED_STATUSES = ['Filled'];
const CANCELLED_STATUSES = ['Cancelled', 'ApiCancelled', 'Inactive'];

const STATUS_FILTERS = [
	{ key: 'ALL', label: 'Alle' },
	{ key: 'OPEN', label: 'Offen' },
	{ key: 'EXECUTED', label: 'Ausgeführt' },
	{ key: 'CANCELLED', label: 'Gestrichen' },
];

function matchesStatusFilter(order, filter) {
	if (filter === 'ALL') return true;
	if (filter === 'OPEN') return OPEN_STATUSES.includes(order.orderstatus);
	if (filter === 'EXECUTED') return EXECUTED_STATUSES.includes(order.orderstatus);
	if (filter === 'CANCELLED') return CANCELLED_STATUSES.includes(order.orderstatus);
	return true;
}

// Aktien-/Kontrakt-Stückzahlen immer ganzzahlig (Nutzervorgabe, Chat vom
// 2026-08-08/10: "Aktien gibt es nicht in Bruchstücken, kleinste Einheit ist
// 1"). Die DB-Spalten totalquantity/filledquantity sind decimal(18,4), IB
// liefert also z.B. "10.0000" - hier auf ganze Stück runden.
function formatQuantity(value) {
	if (value == null || value === '') return '–';
	const parsed = parseFloat(value);
	return Number.isNaN(parsed) ? '–' : Math.round(parsed).toLocaleString('de-DE');
}

// Kurs kompakt (Chat vom 2026-08-10) - decimal(18,4) aus der DB liefert z.B.
// "29832.0000"; hier auf max. 3 Nachkommastellen und ohne ueberfluessige
// Nullen (Nutzervorgabe zu Kurs-Nachkommastellen). Genutzt fuer Limit UND den
// neuen Ausfuehrungskurs (avgfillprice).
function formatPrice(value) {
	if (value == null || value === '') return '–';
	const parsed = parseFloat(value);
	if (Number.isNaN(parsed) || parsed === 0) return '–';
	return parsed.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
}

// Ausfuehrungszeit (Chat vom 2026-08-10) - die ECHTE Boersen-Ausfuehrungszeit
// aus dem IB-execDetails-Event (Spalte exectime), NICHT mehr lastsyncat. Der
// erste Versuch mit lastsyncat war falsch: das ist der Zeitpunkt des letzten
// Sync-Laufs, nicht des Fills (Nutzerbefund - alle Zeilen zeigten dieselbe
// Zeit). Ist exectime noch nicht gefuellt (aeltere Order von vor dieser
// Erweiterung, oder Fill lag ausserhalb des von IB nachladbaren Zeitfensters),
// wird bewusst "–" gezeigt statt eines irrefuehrenden Naeherungswerts. Bewusst
// per String-Zerlegung statt Date() formatiert - die DB liefert Serverlokalzeit
// ohne Zeitzone, ein Date() wuerde je nach Browser-TZ verschieben. Erwartet
// "YYYY-MM-DD HH:MM:SS".
function formatFillTime(value) {
	if (!value || typeof value !== 'string') return '–';
	const parts = value.split(' ');
	if (parts.length < 2) return '–';
	const date = parts[0].split('-');
	if (date.length < 3) return '–';
	return date[2] + '.' + date[1] + '.' + date[0].slice(2) + ' ' + parts[1].slice(0, 8);
}

function statusVariant(orderstatus) {
	if (OPEN_STATUSES.includes(orderstatus)) return 'open';
	if (EXECUTED_STATUSES.includes(orderstatus)) return 'executed';
	if (CANCELLED_STATUSES.includes(orderstatus)) return 'cancelled';
	return 'unknown';
}

export default function IbBrokerageOrderbook(props) {
	const x = props.x;
	const [orders, setOrders] = React.useState(null);
	const [error, setError] = React.useState('');
	// Offene Order ändern (Chat vom 2026-08-10) - oeffnet das IB-Order-Formular
	// im Change-Modus (target.orderId gesetzt), analog Comdirect.
	const [changeTarget, setChangeTarget] = React.useState(null);
	const [cancelling, setCancelling] = React.useState(null);
	// Default "Offen" (analog BrokerageOrderbook.js/Comdirect) - die meist
	// relevanten Orders beim Oeffnen direkt sichtbar.
	const [statusFilter, setStatusFilter] = React.useState('OPEN');
	// Chat vom 2026-08-08 (Bugreport in IbBrokerageOverview.js: "Klick auf
	// Reload-Icon bleibt wirkungslos") - gleicher fehlender visueller
	// Feedback-Zustand wie dort, hier direkt mitgefixt.
	const [reloading, setReloading] = React.useState(false);
	// Chat vom 2026-08-09 (Bugreport: Orderbuch nach Stornieren nicht
	// aktualisiert) - IBs Stornierung ist asynchron: cancelOrder liefert
	// zunaechst nur "PendingCancel"/den Vorher-Status zurueck, der finale
	// "Cancelled"/"ApiCancelled"-orderStatus trifft erst 1-2s spaeter ein und
	// wird dann serverseitig gespiegelt. Ein Sofort-Reload zeigt daher noch
	// den offenen Stand - deshalb zusaetzliche verzoegerte Nachladungen.
	// Timer-Handles zum sauberen Abbruch beim Unmount.
	const recheckTimers = React.useRef([]);

	function scheduleRechecks() {
		recheckTimers.current.forEach(clearTimeout);
		recheckTimers.current = [2000, 5000].map((delay) => setTimeout(loadOrders, delay));
	}

	function loadOrders(onDone) {
		x.was.axPost('Backend/webapp.php?module=IbBrokerage&task=IbBrokerageOrderList', {}, (res) => {
			if (res.data.returncode !== 1) {
				setError('Orderbuch konnte nicht geladen werden.');
				if (onDone) onDone();
				return;
			}
			setOrders(res.data.content.orders || []);
			if (onDone) onDone();
		});
	}

	function reload() {
		setReloading(true);
		// Chat vom 2026-08-12: Reload stiess bisher nur ein DB-Neuladen an - eine
		// erst nach dem Connect (z.B. in der TWS) aufgegebene offene Order tauchte
		// so nie auf. Jetzt erst einen echten IB-Abgleich anstossen
		// (IbBrokerageRefreshOrders -> Node reqAllOpenOrders), dann die Spiegelung
		// laden. Der Sync laeuft async -> mit scheduleRechecks nachfassen.
		x.was.axPost('Backend/webapp.php?module=IbBrokerage&task=IbBrokerageRefreshOrders', {}, (res) => {
			const notConnected = res.data.returncode === 1
				&& res.data.content && res.data.content.connected === false;
			loadOrders(() => setReloading(false));
			scheduleRechecks();
			if (notConnected) {
				x.wah.addHint('IB-Gateway ist nicht verbunden – das Orderbuch konnte gerade nicht mit IB abgeglichen werden.', false);
			} else {
				x.wah.addHint('Orderbuch mit IB abgeglichen.');
			}
		}, () => {
			// Netzwerk-/Serverfehler: trotzdem wenigstens die DB neu laden.
			loadOrders(() => setReloading(false));
			x.wah.addHint('Abgleich mit IB fehlgeschlagen.', false);
		});
	}

	React.useEffect(() => {
		loadOrders();
		const unregister = registerBrokerageDataListener(x, loadOrders);
		return () => {
			unregister();
			recheckTimers.current.forEach(clearTimeout);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Chat vom 2026-08-07 (Nutzerfeedback: "nie diese hässlichen
	// System-Dialoge") - x.wau.yesNo() ist der app-eigene, gestylte
	// Bestätigungsdialog (siehe WebappUtility.js, bereits genutzt in
	// MarketdataGroupSelector.js) statt des browsereigenen window.confirm().
	// Offene Order ändern (Chat vom 2026-08-10) - baut aus der Spiegel-Zeile
	// das Ziel fuers IB-Order-Formular. Der Kontrakt wird ueber die conId
	// eindeutig identifiziert (Boerse/Waehrung sind in der Order-Spiegelung
	// nicht gespeichert - SMART/USD als Nominal-Default; IB nutzt bei gesetzter
	// conId ohnehin diese zur Identifikation). Stückzahl ganzzahlig.
	function openChange(order) {
		setChangeTarget({
			orderId: order.orderid,
			conId: parseInt(order.conid, 10),
			symbol: order.symbol,
			secType: order.sectype,
			exchange: 'SMART',
			currency: 'USD',
			action: order.action,
			orderType: order.ordertype,
			totalQuantity: Math.round(parseFloat(order.totalquantity)) || 1,
			limitPrice: order.limitprice != null && order.limitprice !== ''
				? Math.round(parseFloat(order.limitprice) * 100) / 100 : '',
			label: order.symbol,
		});
	}

	function cancelOrder(orderId) {
		x.wau.yesNo('Order ' + orderId + ' wirklich stornieren?', (result) => {
			if (result !== 1) {
				return;
			}
			setCancelling(orderId);
			x.was.axPost('Backend/webapp.php?module=IbBrokerage&task=IbBrokerageCancelOrder', { orderId }, (res) => {
				setCancelling(null);
				if (res.data.returncode !== 1 || res.data.content?.error) {
					setError(res.data.content?.error || 'Stornieren fehlgeschlagen.');
					return;
				}
				notifyBrokerageDataChanged(x);
				loadOrders();
				// Finaler "Cancelled"-Status kommt async 1-2s spaeter (siehe
				// recheckTimers oben) - nachfassen, sonst bleibt die Order in
				// der Anzeige noch offen.
				scheduleRechecks();
			});
		}, true);
	}

	// Stop-Loss-Orders (isstoploss) sind implizite Auftraege (Chat vom
	// 2026-08-11): sie werden NICHT im Orderbuch gefuehrt, sondern
	// ausschliesslich ueber den Stop-Eintrag im Depot verwaltet - hier komplett
	// ausblenden, damit sie nicht versehentlich hier storniert/geaendert werden.
	const filteredOrders = (orders || []).filter(
		(order) => !parseInt(order.isstoploss, 10) && matchesStatusFilter(order, statusFilter)
	);

	return (
		<Module id={props.id} x={x} classes="module-ib-brokerage-orderbook" title={props.title} display="d-block">
			<div className="brokerage-toolbar">
				<i className={'bi bi-arrow-repeat brokerage-action-icon brokerage-toolbar-reload' + (reloading ? ' brokerage-icon-spin' : '')} title="Neu laden" onClick={reload}></i>
			</div>
			{error !== '' && <p className="brokerage-error"><i className="bi bi-exclamation-triangle"></i> {error}</p>}
			<div className="brokerage-status-filter">
				{STATUS_FILTERS.map((filter) => (
					<span
						key={filter.key}
						className={'brokerage-status-filter-item' + (statusFilter === filter.key ? ' brokerage-status-filter-active' : '')}
						onClick={() => setStatusFilter(filter.key)}
					>
						{filter.label}
					</span>
				))}
			</div>
			{orders === null && <p>Lade…</p>}
			{orders !== null && orders.length === 0 && <p className="brokerage-hint">Noch keine Orders.</p>}
			{orders !== null && orders.length > 0 && filteredOrders.length === 0 &&
				<p className="brokerage-hint">Keine Orders in dieser Kategorie.</p>
			}
			{filteredOrders.length > 0 &&
				<div className="brokerage-table-scroll">
					<div className="brokerage-order-row brokerage-order-row-header">
						<span>Symbol</span>
						<span>Richtung</span>
						<span>Typ</span>
						<span>Stück</span>
						<span>Gefüllt</span>
						<span>Limit</span>
						<span>Ausf.-Kurs</span>
						<span>Zeit</span>
						<span>Status</span>
						<span></span>
					</div>
					{filteredOrders.map((order) => {
						const cancellable = OPEN_STATUSES.includes(order.orderstatus);
						// Direkt in der TWS platzierte Orders wurden mit der permId als
						// orderid-Ersatz gespiegelt (Chat vom 2026-08-12, siehe
						// ibBrokerageSyncOrder.php) - erkennbar an orderid == permid. Bei
						// App-Orders ist orderid klein und != permid. Solche Orders lassen
						// sich NICHT ueber die API aendern/stornieren (IB braucht die echte
						// API-orderId), daher hier nur ein Hinweis statt der Aktions-Icons.
						const isTwsOrder = order.permid != null && order.permid !== ''
							&& String(order.orderid) === String(order.permid);
						return (
							<div className="brokerage-order-row" key={order.id}>
								<span className="brokerage-order-instrument">{order.symbol}</span>
								<span className={order.action === 'SELL' ? 'brokerage-side-sell' : 'brokerage-side-buy'}>{order.action === 'SELL' ? 'Verkauf' : 'Kauf'}</span>
								<span>{order.ordertype}</span>
								<span>{formatQuantity(order.totalquantity)}</span>
								<span>{formatQuantity(order.filledquantity ?? 0)}</span>
								<span>{formatPrice(order.limitprice)}</span>
								<span>{formatPrice(order.avgfillprice)}</span>
								<span className="brokerage-order-time">{formatFillTime(order.exectime)}</span>
								{/* Sprechender Text statt Farbpunkt (Nutzerfeedback, Chat vom
								    2026-08-07: "das mit den verschiedenfarbigen Punkten ist mir
								    nicht klar genug") - direkt als Pille in der Zeile, IB hat
								    (anders als Comdirect) keine eigene aufklappbare
								    Detailansicht, daher hier nicht nur ein Punkt. */}
								<span>
									<span className={'brokerage-status-pill brokerage-status-pill-' + statusVariant(order.orderstatus)}>
										{IB_STATUS_LABELS[order.orderstatus] || order.orderstatus}
									</span>
								</span>
								<span className="brokerage-order-actions">
									{cancellable && isTwsOrder &&
										<i
											className="bi bi-info-circle brokerage-action-icon brokerage-action-tws-hint"
											title="In der TWS platziert – Ändern/Stornieren nur direkt in der TWS möglich."
											onClick={() => x.wah.addHint('Diese Order wurde direkt in der TWS (Handelsplattform) aufgegeben und kann nur dort geändert oder storniert werden – nicht über diese App.', false)}
										></i>
									}
									{cancellable && !isTwsOrder &&
										<React.Fragment>
											<i
												className="bi bi-pencil brokerage-action-icon"
												title="Order ändern"
												onClick={() => openChange(order)}
											></i>
											<i
												className={'bi bi-x-circle brokerage-action-icon brokerage-action-sell' + (cancelling === order.orderid ? ' disabled' : '')}
												title="Order stornieren"
												onClick={() => cancelling !== order.orderid && cancelOrder(order.orderid)}
											></i>
										</React.Fragment>
									}
								</span>
							</div>
						);
					})}
				</div>
			}
			{changeTarget &&
				<div className="brokerage-overlay">
					<IbBrokerageOrderForm
						x={x}
						target={changeTarget}
						onCancel={() => setChangeTarget(null)}
						onDone={() => { setChangeTarget(null); notifyBrokerageDataChanged(x); loadOrders(); scheduleRechecks(); }}
					/>
				</div>
			}
		</Module>
	);
}
